import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { withProjectParam } from '@/lib/projectUrl'
import { useAppStore } from '@/store/appStore'
import { Sidebar } from './Sidebar'
import { Header } from './Header'
import { EnvironmentBanner } from './EnvironmentBanner'
import { MobileTabBar } from './MobileTabBar'
import { AiPanel } from '@/components/ai/AiPanel'
import { CommandPalette } from '@/components/common/CommandPalette'
import { Breadcrumb } from '@/components/common/Breadcrumb'
import { WalkthroughOverlay } from '@/components/walkthrough/WalkthroughOverlay'
import { JourneyRail } from '@/components/walkthrough/JourneyRail'
import { WalkthroughLauncher } from '@/components/walkthrough/WalkthroughLauncher'
import { PageTourOverlay } from '@/components/common/pageTour/PageTourOverlay'
import { PageExploreOverlay } from '@/components/common/pageTour/PageExploreOverlay'
import { usePageTourStore } from '@/components/common/pageTour/pageTourStore'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { applyDateFormatForUser, syncMyProfileFromSupabase } from '@/lib/myProfileStore'

// Routes that are scoped to a single project. Landing on any of these via
// direct URL, refresh, Command Palette, or a non-sidebar entry must flip
// managementContext to 'project' so the project sidebar nav + breadcrumb
// render. Without this guard the user sees portfolio nav on a project page
// and the breadcrumb collapses to nothing.
//
// Exported so `web/src/test/route-context-coverage.test.ts` can verify
// every project-scoped route in `App.tsx` is represented here. Adding a
// new project page to `App.tsx` without adding the prefix below will
// fail that test at vitest time (ProjectTasks #88).
export const PROJECT_ONLY_ROUTE_PREFIXES = [
  '/project-overview',
  '/projects/',           // /projects/:projectId and nested
  '/hierarchy',           // includes /hierarchy/linkages
  '/gantt',
  '/finance',
  '/forecasts',
  '/evm',
  '/team',
  '/reports',
  '/lessons-learned',
  '/materials',
  // Project Studio pages that read selectedProject but were miscategorised as
  // portfolio routes in the coverage registry, so landing on them left the
  // portfolio sidebar up over a project-scoped page - the same defect b5e4d4b
  // fixed for /gantt, reached through a different door (command palette,
  // bookmark, or the ERP Sync button on Currency Rates).
  '/implementation',
  '/erp-sync',
  '/project-fx',
  '/estimate-import',
  '/rates',
  // Agile Delivery surfaces (Hybrid Waterfall/Agile feature).
  '/backlog',
  '/board',
  '/sprints',
  '/roadmap',
  '/agile-insights',
]

function isProjectOnlyRoute(path: string): boolean {
  return PROJECT_ONLY_ROUTE_PREFIXES.some(p => path === p || path.startsWith(p + '/') || (p.endsWith('/') && path.startsWith(p)))
}

// Routes in the personal "My Work" family. Landing on any of these (direct URL,
// the Next-Best-Action card, or from another context) must flip managementContext
// to 'mywork' so the My Work sidebar nav renders — otherwise the page shows the
// previous context's sidebar (e.g. Contract nav over the My Work dashboard, which
// also hid the Skill Approvals item). Same guard pattern as project routes.
const MY_WORK_ROUTE_PREFIXES = ['/my-work', '/my-profile', '/skill-approvals']
function isMyWorkRoute(path: string): boolean {
  return MY_WORK_ROUTE_PREFIXES.some(p => path === p || path.startsWith(p + '/'))
}

// The over-arching Organizations layer. Same guard rationale as My Work: reached
// directly (not via the homepage tile) it must flip to 'organizational' so the
// Organizations sidebar nav renders instead of the previous context's.
const ORGANIZATIONAL_ROUTE_PREFIXES = ['/organizations']
function isOrganizationalRoute(path: string): boolean {
  return ORGANIZATIONAL_ROUTE_PREFIXES.some(p => path === p || path.startsWith(p + '/'))
}

// The Supply Chain Planning module. Landing on any /supply-chain route (the
// homepage tile, a deep link, the command palette, or the My Work portal) must
// flip managementContext to 'supplychain' so the SCP sidebar nav renders instead
// of the previous context's. This URL used to alias the project materials page
// (hence it once lived in PROJECT_ONLY_ROUTE_PREFIXES); the SCP module now owns
// it, and the old materials page keeps its own project-only /materials route.
const SUPPLY_CHAIN_ROUTE_PREFIXES = ['/supply-chain']
function isSupplyChainRoute(path: string): boolean {
  return SUPPLY_CHAIN_ROUTE_PREFIXES.some(p => path === p || path.startsWith(p + '/'))
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const sidebarExpanded = useAppStore((s) => s.sidebarExpanded)
  const aiPanelOpen = useAppStore((s) => s.aiPanelOpen)
  const isMobile = useIsMobile()
  const managementContext = useAppStore((s) => s.managementContext)
  const setManagementContext = useAppStore((s) => s.setManagementContext)
  const location = useLocation()

  // The signed-in user's date format. Applied during render from the cached
  // profile so the first paint already uses it, then again once the profile
  // has been read from the server (a first visit on a new device).
  const currentUserId = useAppStore((s) => s.currentUser?.id)
  const [, setDateFormatRev] = useState(0)
  useMemo(() => applyDateFormatForUser(currentUserId), [currentUserId])
  useEffect(() => {
    if (!currentUserId) return
    let cancelled = false
    const before = applyDateFormatForUser(currentUserId)
    void syncMyProfileFromSupabase(currentUserId).then(() => {
      if (cancelled) return
      if (applyDateFormatForUser(currentUserId) !== before) setDateFormatRev((n) => n + 1)
    })
    return () => { cancelled = true }
  }, [currentUserId])

  // When the Explore overlay's right rail (w-72) is open, reflow the page into
  // the space left of it (lg+) so nothing hides behind the panel and the
  // numbered callouts point at fully-visible sections. The overlay re-measures
  // off the <main> resize. Mobile keeps the rail as an overlay.
  const exploreActive = usePageTourStore((s) => s.activeTourKey !== null && s.mode === 'explore')

  // Centralized context guard: any project-only route forces context=project.
  // Replaces the per-page useEffect pattern (was only on ForecastManagement).
  useEffect(() => {
    if (isProjectOnlyRoute(location.pathname) && managementContext !== 'project') {
      setManagementContext('project')
    } else if (isSupplyChainRoute(location.pathname) && managementContext !== 'supplychain') {
      setManagementContext('supplychain')
    } else if (isMyWorkRoute(location.pathname) && managementContext !== 'mywork') {
      setManagementContext('mywork')
    } else if (isOrganizationalRoute(location.pathname) && managementContext !== 'organizational') {
      setManagementContext('organizational')
    }
  }, [location.pathname, managementContext, setManagementContext])

  // Keep the selected project in the URL of every project-only page, so a
  // bookmark or copied link reopens the same project (ProtectedRoute restores
  // it). /projects/:id pages already carry the id in the path.
  const selectedProjectId = useAppStore((s) => s.selectedProject?.id)
  const navigate = useNavigate()
  useEffect(() => {
    if (!selectedProjectId || !isProjectOnlyRoute(location.pathname) || location.pathname.startsWith('/projects/')) return
    const search = withProjectParam(location.search, selectedProjectId)
    if (search) navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, state: location.state })
  }, [selectedProjectId, location.pathname, location.search, location.hash, location.state, navigate])

  return (
    // h-dvh (dynamic viewport height) so the shell fits the visible area on
    // mobile — 100vh wrongly includes the browser chrome/keyboard and overflows.
    <div className="flex h-dvh overflow-hidden bg-surface-muted">
      <Sidebar />
      <div
        className={cn(
          'flex flex-1 flex-col min-w-0 transition-all duration-300',
          // Mobile: the sidebar is an overlay drawer, so content is full-width.
          isMobile ? 'ml-0' : sidebarExpanded ? 'ml-60' : 'ml-16'
        )}
      >
        <Header />
        <EnvironmentBanner />
        <main
          className={cn(
            // No padding on <main> itself: it is the page's scroll container, and
            // Chromium pins a sticky element inside the scroller's padding, so a
            // frozen table header would sit 24px below the top edge. The page
            // padding lives on the inner wrapper instead (same look).
            'flex-1 transition-[padding] duration-300',
            // Mobile: clip page-level horizontal scroll (wide tables keep their
            // own ResponsiveTable scroll wrapper) + clear the fixed tab bar.
            isMobile ? 'overflow-y-auto overflow-x-hidden pb-[4.5rem]' : 'overflow-auto',
            // Make room for the Explore rail (w-72) on lg+ so the page reflows
            // beside it instead of hiding under it.
            exploreActive && 'lg:pr-[21rem]',
          )}
          role="main"
        >
          <div className="animate-fade-in p-6">
            <Breadcrumb />
            {children}
          </div>
        </main>
      </div>
      <MobileTabBar />
      {aiPanelOpen && <AiPanel />}
      <CommandPalette />
      {/* ProjectTasks #109 — guided-simulation walkthrough. Both render
          themselves only when relevant (sandbox mode / active run). */}
      <WalkthroughLauncher />
      <WalkthroughOverlay />
      {/* Journey checklist rail — persistent whole-flow map while a guided
          journey runs (journeys only; simulations keep the overlay alone). */}
      <JourneyRail />
      {/* Immersive help: in-page guided tour launched from the HowThisWorks
          modal's "Take a guided tour" button. Renders only when a tour is active. */}
      <PageTourOverlay />
      {/* Immersive help: free-explore hover map ("Explore this page"). Renders
          only when explore mode is active. */}
      <PageExploreOverlay />
    </div>
  )
}
